import { useMemo } from 'react';
import type { AppointmentWithPatient } from '../lib/api';

interface Props {
  anchorDate: Date;
  appointments: AppointmentWithPatient[];
  /** Click su block esistente → apri editor view/edit */
  onAppointmentClick: (a: AppointmentWithPatient) => void;
  /**
   * Click su slot vuoto → crea con start=quel slot. La stringa è in formato
   * datetime-local "YYYY-MM-DDTHH:MM" (locale).
   */
  onSlotClick: (isoLocal: string) => void;
}

const WEEKDAY_LABELS = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'];

// Calendar slot grid: 08:00 → 20:00 (12 ore), 1 row per 30 min → 24 rows.
const SLOT_HOUR_START = 8;
const SLOT_HOUR_END = 20;
const SLOTS_PER_HOUR = 2;
const SLOT_MIN = 60 / SLOTS_PER_HOUR;
const TOTAL_SLOTS = (SLOT_HOUR_END - SLOT_HOUR_START) * SLOTS_PER_HOUR;
const SLOT_ROW_PX = 28;   // altezza riga in px (mobile-friendly)

/**
 * Calcola array di 7 giorni della settimana ISO che contiene anchorDate.
 * Ritorna Date object con time=00:00 locale.
 */
function buildWeekDays(anchorDate: Date): Date[] {
  const base = new Date(anchorDate);
  base.setHours(0, 0, 0, 0);
  // ISO: Mon=1, Sun=7
  let dow = base.getDay();
  if (dow === 0) dow = 7;
  const monday = new Date(base);
  monday.setDate(monday.getDate() - (dow - 1));
  const days: Date[] = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date(monday);
    d.setDate(d.getDate() + i);
    days.push(d);
  }
  return days;
}

function isoLocalDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function isoLocalDateTime(d: Date, hour: number, min: number): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const hh = String(hour).padStart(2, '0');
  const mm = String(min).padStart(2, '0');
  return `${y}-${m}-${day}T${hh}:${mm}`;
}

function todayLocalIso(): string {
  const now = new Date();
  return isoLocalDate(now);
}

function formatHm(iso: string): string {
  return new Intl.DateTimeFormat('it-IT', {
    hour: '2-digit', minute: '2-digit', hour12: false,
  }).format(new Date(iso));
}

function bgColorForBlock(status: string, proRole: 'nutri' | 'coach'): { bg: string; border: string; ink: string } {
  if (status === 'canceled') {
    return { bg: 'var(--surface-2)', border: 'var(--mute-3)', ink: 'var(--mute)' };
  }
  if (status === 'completed') {
    return { bg: 'var(--surface-2)', border: 'var(--mute-2)', ink: 'var(--ink-3)' };
  }
  if (status === 'no-show') {
    return { bg: '#fef3c7', border: 'var(--yellow)', ink: 'var(--ink-2)' };
  }
  // scheduled
  if (proRole === 'coach') {
    return { bg: 'var(--orange-tint)', border: 'var(--orange)', ink: 'var(--orange-deep)' };
  }
  return { bg: 'var(--green-tint)', border: 'var(--green)', ink: 'var(--green-deep)' };
}

interface PositionedAppt {
  appt: AppointmentWithPatient;
  startRow: number;     // 0-based row index (0..TOTAL_SLOTS-1)
  rowSpan: number;      // numero righe occupate (clamp ≥1)
}

function positionAppointment(a: AppointmentWithPatient): PositionedAppt | null {
  const start = new Date(a.start_at);
  const hour = start.getHours();
  const min = start.getMinutes();
  if (hour < SLOT_HOUR_START || hour >= SLOT_HOUR_END) {
    // Fuori scala visiva — non visualizziamo nel grid (potrebbe essere notturno)
    // Lo gestiamo nella sezione "fuori-orario" sotto la griglia.
    return null;
  }
  const startSlot = (hour - SLOT_HOUR_START) * SLOTS_PER_HOUR + Math.floor(min / SLOT_MIN);
  const rowSpan = Math.max(1, Math.round(a.duration_min / SLOT_MIN));
  return {
    appt: a,
    startRow: startSlot,
    rowSpan: Math.min(rowSpan, TOTAL_SLOTS - startSlot),
  };
}

/**
 * Raggruppa appuntamenti per giorno e li posiziona nel grid.
 */
function groupAppointmentsByDay(
  appointments: AppointmentWithPatient[],
  weekDays: Date[]
): Map<string, { inGrid: PositionedAppt[]; outOfGrid: AppointmentWithPatient[] }> {
  const map = new Map<string, { inGrid: PositionedAppt[]; outOfGrid: AppointmentWithPatient[] }>();
  for (const d of weekDays) {
    map.set(isoLocalDate(d), { inGrid: [], outOfGrid: [] });
  }
  for (const a of appointments) {
    const start = new Date(a.start_at);
    const key = isoLocalDate(start);
    const entry = map.get(key);
    if (!entry) continue;
    const pos = positionAppointment(a);
    if (pos) {
      entry.inGrid.push(pos);
    } else {
      entry.outOfGrid.push(a);
    }
  }
  return map;
}

export function CalendarWeekView({
  anchorDate,
  appointments,
  onAppointmentClick,
  onSlotClick,
}: Props) {
  const days = useMemo(() => buildWeekDays(anchorDate), [anchorDate]);
  const byDay = useMemo(() => groupAppointmentsByDay(appointments, days), [appointments, days]);
  const todayIso = todayLocalIso();

  // Build row labels (ore)
  const hourLabels: Array<{ label: string; isHour: boolean; slot: number }> = [];
  for (let h = SLOT_HOUR_START; h < SLOT_HOUR_END; h++) {
    hourLabels.push({
      label: `${String(h).padStart(2, '0')}:00`,
      isHour: true,
      slot: (h - SLOT_HOUR_START) * SLOTS_PER_HOUR,
    });
  }

  // Out-of-grid summaries (notte/early morning)
  const hasOutOfGrid = days.some((d) => (byDay.get(isoLocalDate(d))?.outOfGrid.length || 0) > 0);

  return (
    <div
      style={{
        background: 'var(--surface)',
        border: '1px solid var(--line)',
        borderRadius: 14,
        overflow: 'hidden',
      }}
      role="grid"
      aria-label="Calendario settimanale"
    >
      {/* Header row giorni */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '56px repeat(7, 1fr)',
          background: 'var(--surface-2)',
          borderBottom: '1px solid var(--line)',
        }}
        role="row"
      >
        <div style={{ borderRight: '1px solid var(--line)' }} aria-hidden />
        {days.map((d, i) => {
          const iso = isoLocalDate(d);
          const isToday = iso === todayIso;
          return (
            <div
              key={iso}
              role="columnheader"
              style={{
                padding: '10px 12px',
                textAlign: 'center',
                borderRight: i < 6 ? '1px solid var(--line)' : 'none',
              }}
            >
              <div
                style={{
                  fontFamily: 'var(--mono)',
                  fontSize: 10.5,
                  fontWeight: 700,
                  letterSpacing: 1.2,
                  textTransform: 'uppercase',
                  color: 'var(--mute)',
                }}
              >
                {WEEKDAY_LABELS[i]}
              </div>
              <div
                style={{
                  marginTop: 4,
                  fontSize: 18,
                  fontWeight: 800,
                  fontFamily: 'var(--display)',
                  color: isToday ? '#fff' : 'var(--ink)',
                  background: isToday ? 'var(--ink)' : 'transparent',
                  width: 30,
                  height: 30,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '50%',
                  lineHeight: 1,
                }}
              >
                {d.getDate()}
              </div>
            </div>
          );
        })}
      </div>

      {/* Out-of-grid summary band (eventi prima delle 8 o dopo le 20) */}
      {hasOutOfGrid && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '56px repeat(7, 1fr)',
            borderBottom: '1px solid var(--line)',
            background: '#fff8ec',
          }}
        >
          <div
            style={{
              padding: '6px 8px',
              fontSize: 10,
              fontWeight: 700,
              color: 'var(--mute)',
              fontFamily: 'var(--mono)',
              textTransform: 'uppercase',
              borderRight: '1px solid var(--line)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
            }}
          >
            Altri
          </div>
          {days.map((d, i) => {
            const entry = byDay.get(isoLocalDate(d));
            const out = entry?.outOfGrid || [];
            return (
              <div
                key={`out-${i}`}
                style={{
                  padding: '4px 6px',
                  borderRight: i < 6 ? '1px solid var(--line)' : 'none',
                  fontSize: 11,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                }}
              >
                {out.map((a) => (
                  <button
                    key={a.id}
                    type="button"
                    onClick={() => onAppointmentClick(a)}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      color: 'var(--ink-3)',
                      padding: '2px 4px',
                      borderRadius: 4,
                      cursor: 'pointer',
                      fontSize: 11,
                      textAlign: 'left',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                    aria-label={`${a.patient_name} alle ${formatHm(a.start_at)}`}
                  >
                    {formatHm(a.start_at)} {a.patient_name.split(' ')[0]}
                  </button>
                ))}
              </div>
            );
          })}
        </div>
      )}

      {/* Grid orario */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '56px repeat(7, 1fr)',
          position: 'relative',
        }}
      >
        {/* Colonna ore (sticky labels) */}
        <div style={{ borderRight: '1px solid var(--line)', position: 'relative' }}>
          {hourLabels.map((h) => (
            <div
              key={h.slot}
              style={{
                height: SLOT_ROW_PX * SLOTS_PER_HOUR,
                padding: '4px 8px 0',
                fontSize: 10,
                fontFamily: 'var(--mono)',
                fontWeight: 600,
                color: 'var(--mute)',
                textAlign: 'right',
                borderBottom: '1px dashed var(--line-3)',
              }}
            >
              {h.label}
            </div>
          ))}
        </div>

        {/* Colonne giorni */}
        {days.map((d, dayIdx) => {
          const entry = byDay.get(isoLocalDate(d));
          const inGrid = entry?.inGrid || [];

          return (
            <div
              key={`col-${dayIdx}`}
              style={{
                position: 'relative',
                borderRight: dayIdx < 6 ? '1px solid var(--line)' : 'none',
              }}
            >
              {/* Slot rows base (click-empty per creare) */}
              {Array.from({ length: TOTAL_SLOTS }).map((_, slotIdx) => {
                const hour = SLOT_HOUR_START + Math.floor(slotIdx / SLOTS_PER_HOUR);
                const min = (slotIdx % SLOTS_PER_HOUR) * SLOT_MIN;
                const isHourBoundary = min === 0;
                return (
                  <div
                    key={`s-${slotIdx}`}
                    role="button"
                    tabIndex={0}
                    onClick={() => onSlotClick(isoLocalDateTime(d, hour, min))}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onSlotClick(isoLocalDateTime(d, hour, min));
                      }
                    }}
                    aria-label={`Nuovo appuntamento ${isoLocalDateTime(d, hour, min)}`}
                    style={{
                      height: SLOT_ROW_PX,
                      borderBottom: isHourBoundary
                        ? '1px dashed var(--line-3)'
                        : '1px dotted var(--line-3)',
                      cursor: 'pointer',
                      transition: 'background 0.08s',
                    }}
                    onMouseEnter={(e) => {
                      (e.currentTarget as HTMLDivElement).style.background = 'var(--surface-2)';
                    }}
                    onMouseLeave={(e) => {
                      (e.currentTarget as HTMLDivElement).style.background = 'transparent';
                    }}
                  />
                );
              })}

              {/* Absolute-positioned appointment blocks */}
              {inGrid.map((p, blockIdx) => {
                const colors = bgColorForBlock(p.appt.status, p.appt.pro_role);
                const top = p.startRow * SLOT_ROW_PX;
                const height = p.rowSpan * SLOT_ROW_PX - 2;
                return (
                  <button
                    key={p.appt.id}
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onAppointmentClick(p.appt);
                    }}
                    style={{
                      position: 'absolute',
                      left: 3,
                      right: 3,
                      top,
                      height,
                      background: colors.bg,
                      border: `1px solid ${colors.border}`,
                      borderLeft: `3px solid ${colors.border}`,
                      color: colors.ink,
                      padding: '4px 6px',
                      borderRadius: 6,
                      textAlign: 'left',
                      cursor: 'pointer',
                      overflow: 'hidden',
                      fontSize: 11,
                      lineHeight: 1.3,
                      fontFamily: 'var(--sans)',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1,
                      opacity: p.appt.status === 'canceled' ? 0.55 : 1,
                      textDecoration: p.appt.status === 'canceled' ? 'line-through' : 'none',
                      zIndex: 1 + blockIdx,
                    }}
                    aria-label={`${p.appt.patient_name} alle ${formatHm(p.appt.start_at)}, durata ${p.appt.duration_min} minuti`}
                  >
                    <div
                      style={{
                        fontFamily: 'var(--mono)',
                        fontSize: 10,
                        fontWeight: 700,
                        letterSpacing: 0.3,
                      }}
                    >
                      {formatHm(p.appt.start_at)}
                    </div>
                    <div
                      style={{
                        fontWeight: 700,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {p.appt.title || p.appt.patient_name}
                    </div>
                    {height > 36 && (
                      <div
                        style={{
                          opacity: 0.85,
                          fontSize: 10.5,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {p.appt.patient_name}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}
